import { useQuery, useReactiveVar } from "@apollo/client";
import { compact } from "lodash";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Redirect,
  Route,
  Switch,
  matchPath,
  useLocation,
  useParams,
  useRouteMatch,
} from "react-router-dom";
import {
  GetProfileQueryQueryVariables,
  PricingTier,
} from "types/graphql-schema";
import { GetProfileQueryQuery } from "types/graphql-schema";
import { TFLocationState } from "types/topicflow";

import OnboardingProgress from "@apps/home/components/onboarding-progress";
import {
  deriveReportOnboarding,
  isFullyOnboarded,
  isReportJoined,
} from "@apps/home/helpers/onboarding";
import useStartReportOnboarding from "@apps/home/helpers/use-start-report-onboarding";
import {
  MeetingTOCDockContext,
  useTocHasRoom,
} from "@apps/meeting-new/components/meeting-toc-dock-context";
import useLabel from "@apps/use-label/use-label";
import useUiPreferenceCache from "@apps/use-ui-preference-cache/use-ui-preference-cache";
import {
  currentOrganizationVar,
  currentUserVar,
  isAdminVar,
} from "@cache/cache";
import Avatar from "@components/avatar/avatar";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Dropdown from "@components/dropdown/dropdown";
import Layout from "@components/layout/layout";
import { useLink } from "@components/link/link-helpers";
import Loading from "@components/loading/loading";
import { ToggleButtonGroupType } from "@components/toggle-button-group/toggle-button-group";
import { classNames } from "@helpers/css";
import { assertEdgesNonNull, getUrlUsingTypename } from "@helpers/helpers";

import ProfileCareer from "./career-tab/profile-career-tab";
import CoachingPage from "./coaching-tab/profile-coaching-tab";
import ProfileIntroDialog from "./components/profile-intro-dialog";
import ReportOnboardingGate from "./components/report-onboarding-gate";
import getProfileQuery from "./graphql/get-profile-query";
import ImportOneononeDialog from "./oneonone-tab/import-oneonone-dialog";
import { meetingsShowingEmptyCardsVar } from "./oneonone-tab/oneonone-empty-cards-state";
import ProfileOneononeSidebar from "./oneonone-tab/profile-oneonone-sidebar";
import ProfileOneonone from "./oneonone-tab/profile-oneonone-tab";
import useProfileContentTabs from "./overview-tab/components/use-profile-content-tabs";
import ProfileDetailsModal from "./profile-details-modal";
import ProfileEmptyState from "./profile-empty-state";
import ProfilePageSidebar from "./profile-page-sidebar";
import ProfilePrivateNotes from "./profile-private-notes";
import ProfileResponsiveTabs from "./profile-responsive-tabs";
import ProfileWorkHistory from "./profile-work-history";
import ProfileSummarySidebar from "./summary-tab/profile-summary-sidebar";
import ProfileSummaryTab from "./summary-tab/profile-summary-tab";

const ProfilePage = () => {
  const { path } = useRouteMatch();
  const link = useLink();
  const label = useLabel();
  const loadingDetailsText = "Loading details...";
  const location = useLocation<TFLocationState>();
  const { profileId: profileIdStr } = useParams<{
    profileId: string | undefined;
  }>();
  // Reactive so the onboarding gate closes on its own once persistReportOnboarded
  // updates currentUserVar (optimistically), without needing a local flag.
  const currentUser = useReactiveVar(currentUserVar);
  const organization = currentOrganizationVar();
  const profileId = profileIdStr ? parseInt(profileIdStr) : undefined;
  const meetingsShowingEmptyCards = useReactiveVar(
    meetingsShowingEmptyCardsVar,
  );
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  const { data, loading } = useQuery<
    GetProfileQueryQuery,
    GetProfileQueryQueryVariables
  >(getProfileQuery, {
    variables: {
      userId: profileId || -1,
      currentUserId: currentUser.id,
      now: moment().startOf("hour").format(),
    },
    skip: !profileId,
  });
  const queriedProfile = data?.user;
  const profile =
    queriedProfile && queriedProfile.id === profileId
      ? queriedProfile
      : undefined;
  const profileLoading =
    loading || (!!queriedProfile && queriedProfile.id !== profileId);
  const isManager = profile?.managersList.some(
    ({ id }) => id === currentUser.id,
  );
  // Opening a profile that has no 1-on-1 yet may create a draft one on its own
  // (see OneononeMeetingEmptyCards). Only do that for the people the current
  // user actually runs 1-on-1s with: their own profile (the counterpart is
  // their manager) and their direct reports. Everyone else — a peer, a
  // skip-level report, or an external person whose profile is only visible
  // because of a shared meeting — must pick an option first, so browsing a
  // profile never creates a meeting by surprise (TF-1593).
  const canAutoCreateOneonone = profileId === currentUser.id || !!isManager;

  const isAdmin = isAdminVar();
  const canViewCareerTab =
    profile &&
    (isAdmin ||
      profile.isInCurrentUserReportingTree ||
      currentUser.id === profile.id);

  const directReports = assertEdgesNonNull(currentUser.directReports);
  const currentUserIsManagerOfProfile = directReports.find(
    ({ id }) => id === profileId,
  );
  const showPrivateNotes =
    currentUser.id === profile?.id || !!currentUserIsManagerOfProfile;
  const showCoachingTab =
    !!currentUserIsManagerOfProfile &&
    waffle.flag_is_active("coaching-insights");
  const coachingManagerId: number = currentUser.id;
  const canViewPerformanceFeatures =
    organization.effectivePricingTier !== PricingTier.Free;

  const showTabs = profileId === currentUser.id || isManager;
  const showSummaryTab = waffle.flag_is_active("profile-summary") && showTabs;
  const contentTabs = useProfileContentTabs(profile);
  const availableContentTabs = contentTabs.filter((tab) => tab.available);

  const tabButtons: ToggleButtonGroupType<string>[] = compact([
    {
      value: "oneonone",
      title: label("oneonone", { titleCase: true }),
      active: location.pathname.includes(`/profile/${profileId}/oneonone`),
    },
    showSummaryTab && {
      value: "summary",
      title: "Summary",
      active: location.pathname.includes(`/profile/${profileId}/summary`),
    },
    showTabs &&
      showPrivateNotes && {
        value: "my-notes",
        title: "Private Notes",
        active: location.pathname.includes(`/profile/${profileId}/my-notes`),
      },
    showTabs &&
      organization.featureFlags.workHistory && {
        value: "work-history",
        title: "Work History",
        active: location.pathname.includes(
          `/profile/${profileId}/work-history`,
        ),
      },
    showTabs &&
      showCoachingTab && {
        value: "coaching",
        title: "Insights",
        active: location.pathname.includes(`/profile/${profileId}/coaching`),
      },
    ...availableContentTabs.map((tab) => ({
      value: tab.value,
      title: tab.label,
      active: location.pathname === `/profile/${profileId}/${tab.value}`,
    })),
    showTabs &&
      canViewPerformanceFeatures &&
      canViewCareerTab && {
        value: "career",
        title: "Reviews",
        active: location.pathname.includes(`/profile/${profileId}/career`),
      },
  ]);

  const handleTabClick = (button: ToggleButtonGroupType<string>) => {
    // The 1-on-1 tab lands on /profile/:id/oneonone; ProfileOneonone then
    // replaces the URL with the focused meeting id.
    link.redirect(`/profile/${profileId}/${button.value}`);
  };

  const oneononeMatch = matchPath<{ meetingId?: string }>(location.pathname, {
    path: "/profile/:profileId/oneonone/:meetingId?",
  });
  const isOneononeTab = !!oneononeMatch;
  const isSummaryTab = !!matchPath(location.pathname, {
    path: `${path}/summary`,
    exact: true,
  });
  const isProfileOneononeRoute =
    isOneononeTab ||
    !!matchPath(location.pathname, {
      path,
      exact: true,
    });
  const canRenderProfileOneononeRoute =
    isProfileOneononeRoute &&
    profileId !== undefined &&
    (profileLoading || !!profile);
  const selectedMeetingId = oneononeMatch?.params.meetingId
    ? parseInt(oneononeMatch.params.meetingId)
    : null;

  const [useRollingWorkHistoryDateRange, setUseRollingWorkHistoryDateRange] =
    useState(false);

  const sidebarPreferenceKey = "profileSidebarExpanded" as const;

  const { uiPreferenceCache, saveUiPreference } = useUiPreferenceCache();
  const introDialogSeen = uiPreferenceCache.profileIntroDialogSeen;
  const hasMountedRef = useRef(false);
  // The DOM node the floating 1-on-1 TOC portals into. Owned here so its
  // position is decided alongside the sidebar in ONE place (see
  // MeetingTOCDockContext). Only the 1-on-1 tab portals into it; on other
  // tabs the dock stays empty.
  const [tocDockEl, setTocDockEl] = useState<HTMLDivElement | null>(null);
  const hasTocRoom = useTocHasRoom(tocDockEl);
  const sidebarVisible = !!uiPreferenceCache.profileSidebarExpanded;
  const [settledProfileOneononeId, setSettledProfileOneononeId] = useState<
    number | null
  >(null);
  const profileOneononeSettled = settledProfileOneononeId === profileId;
  const handleProfileOneononeSettledChange = useCallback(
    (isSettled: boolean) => {
      setSettledProfileOneononeId(
        isSettled && profileId !== undefined ? profileId : null,
      );
    },
    [profileId],
  );

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }

    setUseRollingWorkHistoryDateRange(false);
  }, [profileId, isOneononeTab]);

  // The intro dialog auto-shows once per user on their first profile visit and
  // is then remembered via the UI preference cache. `introManuallyOpened`
  // tracks the header info button so the dialog can be re-opened on demand
  // after it has been seen.
  const [showIntroDialog, setShowIntroDialog] = useState(false);
  const [introManuallyOpened, setIntroManuallyOpened] = useState(false);
  // While impersonating, never auto-show the intro or persist its "seen" flag:
  // the preference belongs to the impersonated user, so marking it seen here
  // would rob them of their own first-visit intro.
  const isImpersonating = window.TF.hijacked;

  useEffect(() => {
    // Never override a manual open triggered by the header info button.
    if (introManuallyOpened) return;
    // Auto-show for a loaded profile the user hasn't seen the intro for, and
    // auto-close if the value resolves to seen — covers the cache-and-network
    // getLoggedInUser refresh on reload briefly serving a stale "not seen".
    setShowIntroDialog(!!profile && !introDialogSeen && !isImpersonating);
  }, [profile, introDialogSeen, introManuallyOpened, isImpersonating]);

  const handleOpenIntroDialog = () => {
    setIntroManuallyOpened(true);
    setShowIntroDialog(true);
  };

  const handleCloseIntroDialog = () => {
    setIntroManuallyOpened(false);
    setShowIntroDialog(false);
    if (!introDialogSeen && !isImpersonating) {
      saveUiPreference({ profileIntroDialogSeen: true });
    }
  };

  // Report onboarding first-screen: the first time a manager opens a direct report's profile that
  // hasn't been onboarded, offer to walk Topicflow AI through them. The onboarded flag lives on the
  // manager's onboarding_data; either action (start or dismiss) records it so it won't auto-show
  // again — the manager can still re-run the flow from Ask AI anytime.
  const onboardedReportIds = useMemo<number[]>(() => {
    try {
      const data = currentUser.onboardingData
        ? JSON.parse(currentUser.onboardingData)
        : {};
      const ids = data?.onboarded_report_ids;
      return Array.isArray(ids) ? ids.map(Number) : [];
    } catch {
      return [];
    }
  }, [currentUser.onboardingData]);

  // Onboarding progress chip: only shown to a manager viewing their own direct report,
  // and hidden once fully onboarded (matches the homepage direct report card).
  const profileOnboarding = deriveReportOnboarding({
    toldAboutReport:
      profileId !== undefined && onboardedReportIds.includes(profileId),
    hasUpcomingOneonone: (profile?.upcomingOneonone?.edges?.length ?? 0) > 0,
    invited: isReportJoined(profile?.status),
  });
  const showProfileOnboarding =
    !!currentUserIsManagerOfProfile && !isFullyOnboarded(profileOnboarding);

  const { startReportOnboarding, persistReportOnboarded } =
    useStartReportOnboarding();

  // Whether this report's profile should be gated by the onboarding first-screen.
  // Derived (not state) so it disappears the moment the report is marked onboarded.
  // Deliberately independent of `profile` loading — the onboarding decision is known
  // synchronously from currentUser + route params, so we can gate BEFORE the profile
  // query resolves and never mount the 1-on-1 tab behind the gate. Gated behind the
  // same flag as the new direct-reports experience it belongs to, so it rolls out
  // together (and stays out of the way of existing profile tests until enabled).
  const showReportOnboardingGate =
    waffle.flag_is_active("new-homepage-direct-reports") &&
    !!currentUserIsManagerOfProfile &&
    !isImpersonating &&
    profileId !== undefined &&
    !onboardedReportIds.includes(profileId);

  const handleStartReportOnboarding = () => {
    if (profileId === undefined) return;
    startReportOnboarding({ id: profileId, name: profile?.name ?? "" });
  };

  const handleDismissReportOnboarding = () => {
    // Marking onboarded flips showReportOnboardingGate to false, revealing the profile.
    if (profileId !== undefined) persistReportOnboarded(profileId);
  };

  // Gate: for a not-yet-onboarded report, show the onboarding first-screen INSTEAD of
  // the profile so nothing loads behind it. Wait on the profile query (name/avatar)
  // with a plain spinner rather than mounting the profile. Either action marks the
  // report onboarded, which reveals the profile on the next render.
  if (showReportOnboardingGate) {
    return (
      <Layout>
        {profile ? (
          <ReportOnboardingGate
            report={profile}
            onStart={handleStartReportOnboarding}
            onDismiss={handleDismissReportOnboarding}
          />
        ) : (
          <Loading className="flex flex-1 flex-col items-center justify-center">
            Loading...
          </Loading>
        )}
      </Layout>
    );
  }

  return (
    <Layout>
      <MeetingTOCDockContext.Provider
        value={{ dockEl: tocDockEl, hasRoom: hasTocRoom }}
      >
        <Layout.Header
          rightSideClassName="gap-1.5!"
          sidebarExpandedUiPreferenceKey={sidebarPreferenceKey}
          title={
            <div className="flex items-center gap-4 min-w-0 flex-1">
              {profile ? (
                <Button
                  size={ButtonSize.medium}
                  theme={buttonTheme.text}
                  to={getUrlUsingTypename(profile)}
                >
                  <Avatar user={profile} size="4" className="mr-1" />
                  {profile.name}
                </Button>
              ) : (
                <span>Profile</span>
              )}
              {tabButtons.length > 0 && (
                <ProfileResponsiveTabs
                  buttons={tabButtons}
                  onClick={handleTabClick}
                />
              )}
            </div>
          }
        >
          {profile && showProfileOnboarding && (
            <OnboardingProgress
              report={{
                id: profile.id,
                name: profile.name,
                firstName: profile.firstName,
                email: profile.email,
              }}
              onboarding={profileOnboarding}
            />
          )}
          {profile && (
            <Dropdown
              aria-label="Profile actions"
              size={ButtonSize.medium}
              align="end"
              options={compact([
                {
                  label: "View employee details",
                  onClick: () => setIsDetailsModalOpen(true),
                },
                // "Import 1-on-1s" only on a direct report's profile; the
                // "About coaching space" item shows on every profile.
                currentUserIsManagerOfProfile && {
                  label: `Import ${label("oneonone", { capitalize: true, pluralize: true })}`,
                  onClick: () => setIsImportDialogOpen(true),
                },
                {
                  label: "About the coaching space",
                  onClick: handleOpenIntroDialog,
                },
              ])}
            />
          )}
        </Layout.Header>
        {profile && isImportDialogOpen && (
          <ImportOneononeDialog
            profileId={profile.id}
            onClose={() => setIsImportDialogOpen(false)}
          />
        )}
        {profile && isDetailsModalOpen && (
          <ProfileDetailsModal
            profileId={profile.id}
            profileName={profile.name}
            onClose={() => setIsDetailsModalOpen(false)}
          />
        )}
        <Layout.Container>
          <Layout.Main
            // Widen the column for the get-started cards (1-on-1 tab with no
            // meeting focused) and the in-meeting empty cards; a focused
            // meeting uses the narrower reading width.
            width={
              isOneononeTab &&
              (selectedMeetingId === null || meetingsShowingEmptyCards.size > 0)
                ? "xl"
                : "md"
            }
            // The 1-on-1 tab embeds the meeting page content, whose sticky
            // header needs an opaque background to mask the topics scrolling
            // under it — and that background is white to match the standalone
            // meeting page (see meeting-routes.tsx and details.tsx). Paint this
            // scroll area white too, otherwise the header reads as a white box
            // on the default off-white content background (TF-1583).
            className={isOneononeTab ? "bg-white" : undefined}
            sidebarExpandedUiPreferenceKey={sidebarPreferenceKey}
          >
            {profileLoading && !canRenderProfileOneononeRoute ? (
              <Loading>Loading profile...</Loading>
            ) : !profile && !canRenderProfileOneononeRoute ? (
              <div>
                <ProfileEmptyState>Profile not found.</ProfileEmptyState>
              </div>
            ) : (
              <Switch>
                <Route exact path={path}>
                  {profileId !== undefined ? (
                    <Redirect to={`/profile/${profileId}/oneonone`} />
                  ) : (
                    <ProfileEmptyState>Profile not found.</ProfileEmptyState>
                  )}
                </Route>
                <Route path={`${path}/oneonone/:meetingId?`}>
                  {profileId !== undefined ? (
                    <ProfileOneonone
                      key={profileId}
                      profileId={profileId}
                      profileName={profile?.name}
                      profileFirstName={profile?.firstName}
                      profileEmail={profile?.email}
                      profileAvatar={profile?.avatar}
                      profileManager={profile?.primaryManager ?? null}
                      canAutoCreateOneonone={canAutoCreateOneonone}
                      onSettledChange={handleProfileOneononeSettledChange}
                      onUseRollingWorkHistoryDateRangeChange={
                        setUseRollingWorkHistoryDateRange
                      }
                    />
                  ) : (
                    <ProfileEmptyState>Profile not found.</ProfileEmptyState>
                  )}
                </Route>
                {profile && showPrivateNotes && (
                  <Route exact path={`${path}/my-notes`}>
                    <ProfilePrivateNotes
                      profileId={profile.id}
                      disabled={false}
                      showHeader
                    />
                  </Route>
                )}
                {availableContentTabs.map((tab) => (
                  <Route exact key={tab.value} path={`${path}/${tab.value}`}>
                    {tab.render()}
                  </Route>
                ))}
                {profile && (
                  <Route exact path={`${path}/work-history`}>
                    <ProfileWorkHistory profileId={profile.id} />
                  </Route>
                )}
                {profile && showSummaryTab && (
                  <Route exact path={`${path}/summary`}>
                    <ProfileSummaryTab profileId={profile.id} />
                  </Route>
                )}
                {profile && canViewPerformanceFeatures && canViewCareerTab && (
                  <Route exact path={`${path}/career`}>
                    <ProfileCareer profileId={profile.id} />
                  </Route>
                )}
                {profile && showCoachingTab && (
                  <Route exact path={`${path}/coaching`}>
                    <CoachingPage
                      profileId={profile.id}
                      managerId={coachingManagerId}
                    />
                  </Route>
                )}
              </Switch>
            )}
          </Layout.Main>
          {!profile || (isOneononeTab && !profileOneononeSettled) ? (
            <Layout.Sidebar sidebarExpandedUiPreferenceKey="profileSidebarExpanded">
              {isProfileOneononeRoute ? (
                <Loading className="mt-6">{loadingDetailsText}</Loading>
              ) : (
                <div />
              )}
            </Layout.Sidebar>
          ) : isOneononeTab && selectedMeetingId !== null ? (
            // A 1-on-1 meeting is focused: show its sidebar (My Notes / Ask AI).
            <ProfileOneononeSidebar
              key={profile.id}
              meetingId={selectedMeetingId}
              profileId={profile.id}
              loadingText={loadingDetailsText}
              useRollingWorkHistoryDateRange={useRollingWorkHistoryDateRange}
            />
          ) : isSummaryTab ? (
            // Summary tab: a global Ask AI sidebar (falls back to the Details
            // sidebar when Ask AI isn't available for the org).
            <ProfileSummarySidebar profileId={profile.id} />
          ) : (
            // Any other tab, or the 1-on-1 tab with no meeting yet (empty
            // profile): show the profile details sidebar instead of nothing.
            <ProfilePageSidebar profileId={profile.id} />
          )}
          {/* TOC dock — anchored to the sidebar's left edge when the sidebar
            is visible, otherwise pinned 16px from the viewport's right edge.
            One source of truth for TOC positioning instead of two coupled
            offsets (sidebar + TOC) that had to be kept in sync across
            breakpoints. Widths match `widthBreakpointClassName` in Layout:
            w-96 (24rem), xl=w-120 (30rem), 2xl=w-144 (36rem). Only the 1-on-1
            tab portals into this dock; on other tabs it stays empty. */}
          {isOneononeTab && (
            <div
              ref={setTocDockEl}
              className={classNames(
                // Only show the floating TOC once it actually fits in the
                // gutter beside the reading column (see useTocHasRoom). Below
                // that the inline chevrons + calendar dropdown in the meeting
                // header take over (see previous-meetings-popover-panel.tsx).
                hasTocRoom ? "block" : "hidden",
                // Anchored 16px below Layout.Header (which is 56px tall).
                "fixed top-[72px] z-30",
                sidebarVisible
                  ? "right-[calc(24rem+1rem)] xl:right-[calc(30rem+1rem)] 2xl:right-[calc(36rem+1rem)]"
                  : "right-4",
              )}
            />
          )}
        </Layout.Container>
        {showIntroDialog && (
          <ProfileIntroDialog onClose={handleCloseIntroDialog} />
        )}
      </MeetingTOCDockContext.Provider>
    </Layout>
  );
};

export default ProfilePage;
